/**
 * フィールドプロパティパネル - 選択されたフィールドの詳細設定
 */

import { useState, useEffect } from 'react'
import type { FormField } from '../../types/formBuilder'
import { validateFieldName } from '../../services/formBuilder'
import { Toggle } from '../common/Toggle'

interface FieldPropsPanelProps {
  field: FormField | null
  existingFieldNames: string[]
  onUpdateField: (updatedField: FormField) => void
}

export function FieldPropsPanel({ field, existingFieldNames, onUpdateField }: FieldPropsPanelProps) {
  const [localField, setLocalField] = useState<FormField | null>(field)
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    setLocalField(field)
    setNameError(null)
  }, [field])

  if (!localField) {
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '0.25rem'
          }}>フィールド設定</h3>
          <p style={{
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>選択されたフィールドの設定を編集</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-500 text-center">
            フィールドを選択してください
          </p>
        </div>
      </div>
    )
  }

  const handleChange = (updates: Partial<FormField>) => {
    const updated = { ...localField, ...updates } as FormField
    setLocalField(updated)
    onUpdateField(updated)
  }

  const handleNameChange = (newName: string) => {
    const otherNames = existingFieldNames.filter(n => n !== localField.name)
    const error = validateFieldName(newName, otherNames, localField.id)
    setNameError(error)
    handleChange({ name: newName })
  }

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      padding: '1rem'
    }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#374151',
          marginBottom: '0.25rem'
        }}>フィールド設定</h3>
        <p style={{
          fontSize: '0.75rem',
          color: '#6b7280'
        }}>選択されたフィールドの設定を編集</p>
      </div>

      {/* 基本設定セクション */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#111827',
          marginBottom: '0.75rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>基本設定</h4>

        <div className="space-y-4">
          {/* ラベル */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              ラベル <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={localField.label}
              onChange={(e) => handleChange({ label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="ユーザーに表示される名前"
            />
          </div>

          {/* フィールド名 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              フィールド名 <span className="text-red-600">*</span> <span className="text-gray-500">(英数字とアンダースコア)</span>
            </label>
            <input
              type="text"
              value={localField.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                nameError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-600'
              }`}
              placeholder="データ保存時の識別子（英数字とアンダースコア）"
            />
            {nameError && (
              <p className="text-xs text-red-600 mt-1">{nameError}</p>
            )}
          </div>

          {/* プレースホルダー（text, textarea, number） */}
          {(localField.type === 'text' || localField.type === 'textarea' || localField.type === 'number') && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">プレースホルダー</label>
              <input
                type="text"
                value={localField.placeholder || ''}
                onChange={(e) => handleChange({ placeholder: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="入力欄に表示するヒントテキスト"
              />
            </div>
          )}

          {/* 説明 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">説明</label>
            <textarea
              value={localField.description || ''}
              onChange={(e) => handleChange({ description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="フィールドの説明を記述してください"
            />
          </div>

          {/* 必須入力トグル */}
          <div>
            <Toggle
              checked={localField.required || false}
              onChange={(checked) => handleChange({ required: checked })}
              label="入力を必須にする"
            />
          </div>
        </div>
      </div>

      {/* 入力制限セクション */}
      {(localField.type === 'text' || localField.type === 'textarea' || localField.type === 'number') && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>入力制限</h4>

          <div className="space-y-4">
            {/* テキスト長制限（text, textarea） */}
            {(localField.type === 'text' || localField.type === 'textarea') && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">最小文字数</label>
                  <input
                    type="number"
                    min="0"
                    value={localField.minLength ?? ''}
                    onChange={(e) => handleChange({ minLength: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">最大文字数</label>
                  <input
                    type="number"
                    min="0"
                    value={localField.maxLength ?? ''}
                    onChange={(e) => handleChange({ maxLength: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>
            )}

            {/* 数値範囲（number） */}
            {localField.type === 'number' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">最小値</label>
                  <input
                    type="number"
                    value={localField.minimum ?? ''}
                    onChange={(e) => handleChange({ minimum: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">最大値</label>
                  <input
                    type="number"
                    value={localField.maximum ?? ''}
                    onChange={(e) => handleChange({ maximum: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 時刻設定セクション（time） */}
      {localField.type === 'time' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>時刻設定</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">デフォルト値</label>
              <input
                type="time"
                value={localField.defaultValue || ''}
                onChange={(e) => handleChange({ defaultValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最小時刻</label>
                <input
                  type="time"
                  value={localField.minTime || ''}
                  onChange={(e) => handleChange({ minTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最大時刻</label>
                <input
                  type="time"
                  value={localField.maxTime || ''}
                  onChange={(e) => handleChange({ maxTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">刻み幅（分）</label>
              <input
                type="number"
                min="1"
                max="60"
                value={localField.stepMinutes ?? ''}
                onChange={(e) => handleChange({ stepMinutes: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="例：5（5分刻み）"
              />
            </div>
          </div>
        </div>
      )}

      {/* 日時設定セクション（datetime） */}
      {localField.type === 'datetime' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>日時設定</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">デフォルト値</label>
              <input
                type="datetime-local"
                value={localField.defaultValue || ''}
                onChange={(e) => handleChange({ defaultValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最小日時</label>
                <input
                  type="datetime-local"
                  value={localField.minDateTime || ''}
                  onChange={(e) => handleChange({ minDateTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最大日時</label>
                <input
                  type="datetime-local"
                  value={localField.maxDateTime || ''}
                  onChange={(e) => handleChange({ maxDateTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 選択肢（select） */}
      {localField.type === 'select' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>選択肢</h4>

          <div className="space-y-3">
            {localField.options?.map((opt, idx) => (
              <div key={idx} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="text-xs font-medium text-gray-600">
                    選択肢 {idx + 1}
                  </span>
                  <button
                    onClick={() => {
                      const newOptions = localField.options?.filter((_, i) => i !== idx) || []
                      handleChange({ options: newOptions })
                    }}
                    className="text-red-600 hover:text-red-800 text-xs px-2 border border-red-300 rounded hover:bg-red-50"
                  >
                    削除
                  </button>
                </div>
                <div>
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => {
                      const newOptions = [...(localField.options || [])]
                      newOptions[idx] = { ...newOptions[idx], label: e.target.value, value: `option_${idx + 1}` }
                      handleChange({ options: newOptions })
                    }}
                    placeholder="例：選択肢1"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => {
                const currentOptions = localField.options || []
                const newOptions = [...currentOptions, { label: '', value: `option_${currentOptions.length + 1}` }]
                handleChange({ options: newOptions })
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + 選択肢を追加
            </button>
          </div>
        </div>
      )}

      {/* 選択肢（radio） */}
      {localField.type === 'radio' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>選択肢</h4>

          <div className="space-y-3">
            {localField.options?.map((opt, idx) => (
              <div key={idx} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="text-xs font-medium text-gray-600">
                    選択肢 {idx + 1}
                  </span>
                  <button
                    onClick={() => {
                      const newOptions = localField.options?.filter((_, i) => i !== idx) || []
                      handleChange({ options: newOptions })
                    }}
                    className="text-red-600 hover:text-red-800 text-xs px-2 border border-red-300 rounded hover:bg-red-50"
                  >
                    削除
                  </button>
                </div>
                <div>
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => {
                      const newOptions = [...(localField.options || [])]
                      newOptions[idx] = { ...newOptions[idx], label: e.target.value, value: `option_${idx + 1}` }
                      handleChange({ options: newOptions })
                    }}
                    placeholder="例：選択肢1"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => {
                const currentOptions = localField.options || []
                const newOptions = [...currentOptions, { label: '', value: `option_${currentOptions.length + 1}` }]
                handleChange({ options: newOptions })
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + 選択肢を追加
            </button>
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <label className="block text-xs font-medium text-gray-600 mb-1">デフォルト値</label>
            <select
              value={localField.defaultValue || ''}
              onChange={(e) => handleChange({ defaultValue: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="">未選択</option>
              {localField.options?.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* 選択肢（multiselect） */}
      {localField.type === 'multiselect' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>選択肢</h4>

          <div className="space-y-3">
            {localField.options?.map((opt, idx) => (
              <div key={idx} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="text-xs font-medium text-gray-600">
                    選択肢 {idx + 1}
                  </span>
                  <button
                    onClick={() => {
                      const newOptions = localField.options?.filter((_, i) => i !== idx) || []
                      handleChange({ options: newOptions })
                    }}
                    className="text-red-600 hover:text-red-800 text-xs px-2 border border-red-300 rounded hover:bg-red-50"
                  >
                    削除
                  </button>
                </div>
                <div>
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => {
                      const newOptions = [...(localField.options || [])]
                      newOptions[idx] = { ...newOptions[idx], label: e.target.value, value: `option_${idx + 1}` }
                      handleChange({ options: newOptions })
                    }}
                    placeholder="例：選択肢1"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => {
                const currentOptions = localField.options || []
                const newOptions = [...currentOptions, { label: '', value: `option_${currentOptions.length + 1}` }]
                handleChange({ options: newOptions })
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + 選択肢を追加
            </button>
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <label className="block text-xs font-medium text-gray-600 mb-1">デフォルト値（複数選択可）</label>
            <div className="space-y-2 border border-gray-300 rounded-md p-3 bg-white max-h-40 overflow-y-auto">
              {localField.options?.map((opt, idx) => (
                <div key={idx} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`multiselect-default-${idx}`}
                    checked={localField.defaultValue?.includes(opt.value) || false}
                    onChange={(e) => {
                      const currentDefaults = localField.defaultValue || []
                      const newDefaults = e.target.checked
                        ? [...currentDefaults, opt.value]
                        : currentDefaults.filter(v => v !== opt.value)
                      handleChange({ defaultValue: newDefaults })
                    }}
                    className="mr-2"
                  />
                  <label htmlFor={`multiselect-default-${idx}`} className="text-sm text-gray-700">
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* プレビューセクション */}
      <div>
        <h4 style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#111827',
          marginBottom: '0.75rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>プレビュー</h4>

        <div style={{
          padding: '1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            {localField.label || '（ラベル未設定）'}
            {localField.required && <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>}
          </label>
          {localField.description && (
            <p style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.5rem'
            }}>{localField.description}</p>
          )}
          {localField.type === 'text' && (
            <input
              type="text"
              placeholder={localField.placeholder || ''}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'textarea' && (
            <textarea
              placeholder={localField.placeholder || ''}
              rows={3}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'number' && (
            <input
              type="number"
              placeholder={localField.placeholder || ''}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'select' && (
            <select
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            >
              <option>選択してください</option>
              {localField.options?.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          {localField.type === 'date' && (
            <input
              type="date"
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'checkbox' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                style={{
                  width: '1rem',
                  height: '1rem'
                }}
              />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>チェックボックス</span>
            </div>
          )}
          {localField.type === 'time' && (
            <input
              type="time"
              value={localField.defaultValue || ''}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'datetime' && (
            <input
              type="datetime-local"
              value={localField.defaultValue || ''}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            />
          )}
          {localField.type === 'radio' && (
            <div className="space-y-2">
              {localField.options?.map((opt, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="preview-radio"
                    value={opt.value}
                    checked={localField.defaultValue === opt.value}
                    style={{
                      width: '1rem',
                      height: '1rem'
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>{opt.label}</span>
                </div>
              ))}
            </div>
          )}
          {localField.type === 'multiselect' && (
            <div className="space-y-2">
              {localField.options?.map((opt, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={localField.defaultValue?.includes(opt.value) || false}
                    style={{
                      width: '1rem',
                      height: '1rem'
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>{opt.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
